import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Column,
  FormGroup,
  Stack,
  ToastNotification,
  TextArea,
  TextInput,
 // DatePicker,
  // DatePickerInput,
  InlineLoading,
} from "@carbon/react";
import CommonFields from "../common/CommonField";

const ResaleDetails = ({
  data = {},
  attributeValues = [],
  propertyUuid,
  currentStep = 3,
  totalSteps = 7,
  onBack,
  onSaveSuccess,
  onNavigateNext,
  CommonButtons,
  saveSectionAttributes,
  isEditMode = false,
  isLoading = false,
}) => {
  // ---------- STATE ----------
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [touched, setTouched] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  const [form, setForm] = useState({
    expectedPrice: "",
    priceNegotiable: false,
    availableFrom: "",
    currentlyUnderLoan: false,
    description: "",
  });

  // ---------- FIELD MAPPING ----------
  const FIELD_TO_ATTRIBUTE_MAP = useMemo(
    () => ({
      expectedPrice: "land_resale_expected_price",
      priceNegotiable: "land_resale_price_negotiable",
      availableFrom: "land_resale_available_from",
      currentlyUnderLoan: "land_resale_currently_under_loan",
      description: "land_resale_description",
    }),
    []
  );

  // ---------- COMMON FIELDS CONFIG ----------
  const normalize = useCallback(
    (s) => String(s || "").trim().toLowerCase().replace(/[\s_]/g, ""),
    []
  );

  const getOptions = useCallback(
    (fieldKey) => {
      const match = normalize(FIELD_TO_ATTRIBUTE_MAP[fieldKey] || fieldKey);
      return attributeValues.filter(
        (it) => it?.isActive && normalize(it.attrName) === match
      );
    },
    [attributeValues, FIELD_TO_ATTRIBUTE_MAP, normalize]
  );

  const requiredLabel = (text) => (
    <>
      {text}
    </>
  );

  // ---------- VALIDATION RULES ----------
  const validationRules = useMemo(
    () => ({
      expectedPrice: {
        required: true,
        message: "Expected Price is required",
        custom: (v) => {
          const value = String(v || "").trim();
          if (!value || value === "") return "Expected Price is required";
          if (isNaN(value) || Number(value) <= 0) return "Expected Price must be greater than 0";
          return null;
        },
      },
    }),
    []
  );

  const getFieldError = useCallback(
    (field) => {
      const rule = validationRules[field];
      if (!rule) return null;

      const val = form[field];

      if (rule.required) {
        if (val === "" || val === null || val === undefined) return rule.message;
      }

      if (rule.custom) return rule.custom(val);
      return null;
    },
    [form, validationRules]
  );

  const handleBlur = useCallback(
    (field) => setTouched((prev) => ({ ...prev, [field]: true })),
    []
  );

  // ---------- UPDATE FIELD ----------
  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  // ---------- INITIALIZE FORM ----------
  useEffect(() => {
    if (isInitialized) return;

    const attributes = data.attributes || {};
    const initialForm = {
      expectedPrice: "",
      priceNegotiable: false,
      availableFrom: "",
      currentlyUnderLoan: false,
      description: "",
    };

    Object.entries(FIELD_TO_ATTRIBUTE_MAP).forEach(([fieldKey, dbKey]) => {
      const val = attributes[dbKey];

      if (val !== undefined && val !== null) {
        if (fieldKey === "priceNegotiable" || fieldKey === "currentlyUnderLoan") {
          initialForm[fieldKey] = val === "Yes" || val === true || val === "true";
        } else {
          initialForm[fieldKey] = val;
        }
      }
    });

    console.log("💧 Initializing ResaleDetails form:", initialForm);
    setForm(initialForm);
    setIsInitialized(true);
  }, [data, FIELD_TO_ATTRIBUTE_MAP, isInitialized]);

  // ---------- DATE HANDLER ----------
  const handleDateChange = useCallback(
    ([selectedDate]) => {
      if (!selectedDate) {
        updateField("availableFrom", "");
        return;
      }

      try {
        const day = String(selectedDate.getDate()).padStart(2, "0");
        const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
        const year = selectedDate.getFullYear();
        const formatted = `${day}/${month}/${year}`;

        updateField("availableFrom", formatted);
      } catch (err) {
        console.error("Date formatting error:", err);
        updateField("availableFrom", "");
      }
    },
    [updateField]
  );

  // Convert string date to Date object for DatePicker
  const getDateValue = useCallback(() => {
    if (!form.availableFrom) return null;

    try {
      const [day, month, year] = form.availableFrom.split("/");
      return new Date(`${year}-${month}-${day}`);
    } catch (err) {
      console.error("Date parsing error:", err);
      return null;
    }
  }, [form.availableFrom]);

  // ---------- SAVE FUNCTION ----------
  const validateForm = useCallback(() => {
    const requiredFields = Object.keys(validationRules);
    const newTouched = { ...touched };
    requiredFields.forEach((field) => {
      newTouched[field] = true;
    });
    setTouched(newTouched);

    const hasErrors = requiredFields.some((field) => !!getFieldError(field));
    return !hasErrors;
  }, [touched, validationRules, getFieldError]);

  const prepareAttributes = useCallback(() => {
    const attributes = {};

    Object.entries(FIELD_TO_ATTRIBUTE_MAP).forEach(([key, dbKey]) => {
      const val = form[key];

      const hasValue = val !== "" && val !== null && val !== undefined;

      if (key === "priceNegotiable" || key === "currentlyUnderLoan") {
        attributes[dbKey] = val ? "Yes" : "No";
      } else if (hasValue) {
        if (key === "expectedPrice" && !isNaN(val) && val !== "") {
          attributes[dbKey] = Number(val);
        } else {
          attributes[dbKey] = val;
        }
      }
    });

    return attributes;
  }, [form, FIELD_TO_ATTRIBUTE_MAP]);

  const handleDateInputKeyDown = useCallback((e) => {
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End', 'PageUp', 'PageDown'
    ];

    if (allowedKeys.includes(e.key)) {
      return;
    }

    if (!/[\d/]/.test(e.key)) {
      e.preventDefault();
    }
  }, []);

  const handleSaveInternal = useCallback(
    async (navigateNext = false) => {
      console.log("💾 Starting ResaleDetails save...", {
        propertyUuid,
        navigateNext,
        form,
      });

      if (!validateForm()) {
        setError("Please fill all required fields correctly");
        return false;
      }

      if (!propertyUuid) {
        setError("Property UUID is required to save details");
        return false;
      }

      setSaving(true);
      setError("");
      setSuccess("");

      try {
        const attributes = prepareAttributes();

        console.log("💾 Saving ResaleDetails attributes:", attributes);
        await saveSectionAttributes(propertyUuid, attributes, form, "resale_details");

        setSuccess("Resale details saved successfully!");

        if (navigateNext && onNavigateNext) {
          console.log("➡️ Navigating to next step...");
          onNavigateNext(form);
        }

        if (onSaveSuccess) {
          await onSaveSuccess(propertyUuid, form, currentStep);
        }

        return true;
      } catch (err) {
        console.error("❌ Save failed:", err);
        setError(err.message || "Failed to save resale details");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [
      form,
      validateForm,
      propertyUuid,
      prepareAttributes,
      saveSectionAttributes,
      onNavigateNext,
      onSaveSuccess,
      currentStep,
    ]
  );

  const handleSaveOnly = useCallback(() => handleSaveInternal(false), [handleSaveInternal]);
  const handleSaveAndNext = useCallback(() => handleSaveInternal(true), [handleSaveInternal]);

  const isSaveLoading = saving || isLoading;

  // ---------- RENDER COMMON FIELDS ----------
  const { renderNumberField, renderCheckbox } = CommonFields({
    form,
    touched,
    getFieldError,
    handleBlur,
    updateField,
    getOptions,
  });

  // ---------- RENDER ----------
  if (isLoading && !propertyUuid) {
    return (
      <div style={{ padding: "4rem", textAlign: "center" }}>
        <InlineLoading description="Loading resale details..." />
      </div>
    );
  }

  return (
    <>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          lowContrast
          subtitle={error}
          timeout={5000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          lowContrast
          title="Success"
          subtitle={success}
          timeout={3000}
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
        <Grid>
          <Column sm={4} md={8} lg={16}>
            <div style={{ padding: "2rem" }}>
              <Stack gap={6}>
                {/* PRICE + PRICE NEGOTIABLE */}
                <FormGroup>
                  <Grid>
                    <Column lg={8}>
                      {renderNumberField(
  "expectedPrice",
  "Expected Price (₹ per Cent))",
  "Enter price per cent",
  true
)}
                      {renderCheckbox("priceNegotiable", "Price Negotiable", false)}
                    </Column>
                    {/* AVAILABLE FROM + UNDER LOAN */}
                    <Column lg={8}>
                      <TextInput
                        id="availableFrom"
                        type="date"
                        labelText="Available From"
                        value={form.availableFrom || ""}
                        onChange={(e) => updateField("availableFrom", e.target.value)}
                        onBlur={() => handleBlur("availableFrom")}
                      />
                      <div style={{ marginTop: "1rem" }}>
                        {renderCheckbox("currentlyUnderLoan", "Currently Under Loan", false)}
                      </div>
                    </Column>
                  </Grid>
                </FormGroup>

               
                {/* DESCRIPTION */}
                <FormGroup>
                  <TextArea
                    id="description"
                    labelText="Description"
                    value={form.description}
                    onChange={(e) => updateField("description", e.target.value)}
                    placeholder="Write a few lines about your property something which is special and makes your property stand out. Please do not mention your contact details in any format."
                    rows={4}
                  />
                </FormGroup>

                {/* BUTTONS */}
                {CommonButtons && (
                  <CommonButtons
                    onBack={onBack}
                    onSave={handleSaveOnly}
                    onNext={handleSaveAndNext}
                    isSaveLoading={isSaveLoading}
                    stepData={form}
                  />
                )}
              </Stack>
            </div>
          </Column>
        </Grid>
      </div>
    </>
  );
};

export default ResaleDetails;